import React from 'react';

import { Label } from 'cpool-components/general/Label';
import { TextInput } from 'cpool-components/input/TextInput';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';
import { GERMAN_POSTAL_CODE_ERROR_KEY, isGermanPostalCode } from '~/features/leadDetails/utils/germanPostalCode';
import { keepDigits } from '~/features/leadDetails/utils/keepDigits';

type Props = {
    firstTitle: string;
    secondTitle: string;
    firstInputName: string;
    secondInputName: string;
    firstPlaceholder?: string;
    secondPlaceholder?: string;
    firstKeepDigits?: number;
    swapSize?: boolean;
    disabled?: boolean;
};

const TitleComposedInput: React.FC<Props> = ({
    disabled = false,
    firstInputName,
    firstKeepDigits,
    firstPlaceholder,
    firstTitle,
    secondInputName,
    secondPlaceholder,
    secondTitle,
    swapSize = false,
}) => {
    const { control } = useFormContext();
    const { applicantId } = useApplicantInfoContext();
    const { t } = useTranslation();

    return (
        <div data-testid={firstTitle} className="flex items-start gap-2.5">
            <Label className="h-8 w-[175px] shrink-0 justify-start">
                {firstTitle}
                {', '}
                {secondTitle}
            </Label>
            <div className="flex w-full items-start gap-2.5">
                <FormField<FieldValues, string | null> control={control} name={firstInputName}>
                    {({ error, onBlur, onChange, value }) => (
                        <TextInput
                            id={`${firstInputName}-${applicantId}`}
                            className={swapSize ? 'w-4/12' : 'w-8/12'}
                            inputMode={firstKeepDigits === undefined ? undefined : 'numeric'}
                            maxLength={firstKeepDigits}
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(event) => {
                                if (firstKeepDigits === undefined) {
                                    onChange(event.target.value);
                                    return;
                                }
                                onChange(keepDigits(event.target.value, firstKeepDigits) || null);
                            }}
                            disabled={disabled}
                            error={
                                firstKeepDigits === 5 && !isGermanPostalCode(value)
                                    ? t(GERMAN_POSTAL_CODE_ERROR_KEY)
                                    : error
                            }
                            placeholder={firstPlaceholder}
                        />
                    )}
                </FormField>
                <FormField<FieldValues, string | null> control={control} name={secondInputName}>
                    {({ error, onBlur, onChange, value }) => (
                        <TextInput
                            id={`${secondInputName}-${applicantId}`}
                            className={swapSize ? 'w-8/12' : 'w-4/12'}
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(event) => onChange(event.target.value)}
                            disabled={disabled}
                            error={error}
                            placeholder={secondPlaceholder}
                        />
                    )}
                </FormField>
            </div>
        </div>
    );
};

export default TitleComposedInput;
